import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { darkTheme } from '../../lib/tokens';
import { fetchOverview } from '../analytics/api';
import { useAppContextStore } from '../app/appContextStore';
import { ALL_APPS, DEFAULT_APP_ID } from '../../lib/appConstants';
import { canViewPath, useAdminRole } from '../auth/permissions';

const QUICK_LINKS = [
  {
    to: '/health',
    title: 'System-Health',
    subtitle: 'Provider-Status, Plattform-Probes, KI-Auslastung',
  },
  {
    to: '/feedback',
    title: 'Feedback-Inbox',
    subtitle: 'Triage offener Beta- und App-Feedbacks',
  },
  {
    to: '/users',
    title: 'Benutzer-Verwaltung',
    subtitle: 'Liste, Detail, Suspend, Subscription-Override',
  },
  {
    to: '/announcements',
    title: 'Announcements',
    subtitle: 'In-App-News, Updates, Aktionen',
  },
];

export function HomePage(): JSX.Element {
  const ctxApp = useAppContextStore((s) => s.appId);
  const app = ctxApp === ALL_APPS ? DEFAULT_APP_ID : ctxApp;
  const role = useAdminRole();
  // Kein Link auf eine Seite, die die Rolle ohnehin nicht oeffnen darf —
  // sonst landet sie ueber den Umweg der Umleitung wieder hier (CUR-995).
  const quickLinks = QUICK_LINKS.filter((l) => canViewPath(role, l.to));

  const overview = useQuery({
    queryKey: ['analytics', 'overview', app],
    queryFn: () => fetchOverview(app),
    refetchInterval: 5 * 60_000,
  });

  return (
    <div className="mx-auto max-w-7xl">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">
          Hallo Fabian — heute am Wichtigsten:
        </h1>
        <p className="mt-1 text-xs text-text-tertiary">
          Aktualisiert alle 5 min · Vollständige Analytics in{' '}
          <Link to="/analytics" className="text-accent-light hover:underline">
            Eigene KPIs
          </Link>
        </p>
      </header>

      <section className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        <Tile
          label="Aktive User (24 h)"
          value={overview.data?.dau}
          loading={overview.isLoading}
        />
        <Tile
          label="Neue Signups heute"
          value={overview.data?.signupsToday}
          loading={overview.isLoading}
        />
        <Tile
          label="Pro aktiv"
          value={overview.data?.paidSubscribersActive}
          loading={overview.isLoading}
          accent
        />
        <Tile
          label="Lektionen heute"
          value={overview.data?.lessonsCompletedToday}
          loading={overview.isLoading}
        />
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {quickLinks.map((link) => (
          <QuickLinkCard
            key={link.to}
            to={link.to}
            title={link.title}
            subtitle={link.subtitle}
          />
        ))}
      </section>
    </div>
  );
}

function Tile({
  label,
  value,
  loading,
  accent,
}: {
  label: string;
  value: number | undefined;
  loading: boolean;
  accent?: boolean;
}): JSX.Element {
  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: accent ? darkTheme.colors.accentSubtle : darkTheme.colors.bgSurface,
        borderColor: accent ? darkTheme.colors.accent : darkTheme.colors.borderSubtle,
      }}
    >
      <div className="text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
        {label}
      </div>
      {loading ? (
        <div className="mt-2 h-7 w-16 animate-pulse rounded bg-bg-elevated" />
      ) : (
        <div
          className="mt-1 text-3xl font-bold tabular"
          style={{ color: accent ? darkTheme.colors.accentLight : darkTheme.colors.textPrimary }}
        >
          {value !== undefined ? value.toLocaleString('de-DE') : '—'}
        </div>
      )}
    </div>
  );
}

function QuickLinkCard({
  to,
  title,
  subtitle,
}: {
  to: string;
  title: string;
  subtitle: string;
}): JSX.Element {
  return (
    <Link
      to={to}
      className="block rounded-md border p-4 transition-colors hover:bg-bg-elevated"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="font-semibold text-text-primary">{title}</div>
      <div className="mt-1 text-xs text-text-secondary">{subtitle}</div>
      <div className="mt-2 text-xs text-accent-light">Öffnen →</div>
    </Link>
  );
}
